import { Box, styled } from '@mui/material';
import { darken } from '@mui/material/styles';
import { LineChartProps, lineClasses } from '@mui/x-charts';
import { MarkShape } from '@mui/x-charts/models';
import React, { useId, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import ChartLegend from 'components/charts/chart-legend';
import { SERIES_HIGHLIGHT } from 'components/charts/constants/chart-defaults';
import LineChart from 'components/charts/line-chart';
import { useChartGradient } from 'components/charts/hooks/use-chart-gradient';
import { useChartPalette } from 'components/charts/hooks/use-chart-palette';
import { useChartAnimation } from 'components/charts/hooks/use-chart-animation';
import { useLegendHighlight } from 'components/charts/hooks/use-legend-highlight';
import BenchmarkChartHiddenSeriesOverlay from 'views/benchmark-analytics/components/benchmark-chart-hidden-series-overlay';
import BenchmarkChartTooltip from 'views/benchmark-analytics/components/benchmark-chart-tooltip';

const DASH_PATTERN = '6 4';
const LEGEND_DASH_PATTERN = '2 2';
const AUTO_MARKS_MAX_POINTS = 60;
const TARGET_TICK_COUNT = 5;

type TrendSeries = {
  id: string;
  label: string;
  color: string;
  shape?: MarkShape;
  dashed?: boolean;
  data: (number | null)[];
};

type TrendLineChartProps = {
  points: string[];
  series: TrendSeries[];
  yAxisLabel?: string;
  valueFormatter?: (value: number | null) => string;
  height?: number;
  yAxisMin?: 'auto' | 'zero';
  area?: 'none' | 'gradient' | 'ridge';
  showMarks?: boolean | 'auto';
  hiddenIds?: string[];
  onToggleSeries?: (id: string) => void;
  legend?: boolean;
  lineChartProps?: Partial<LineChartProps>;
  children?: React.ReactNode;
};

// The shared legend knows nothing about dashes. Dash the line glyph of the matching entries, as SeriesMarker does.
const LegendFrame = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'dashedIndexes',
})<{ dashedIndexes: number[] }>(({ dashedIndexes }) =>
  Object.fromEntries(
    dashedIndexes.map((index) => [
      `& button:nth-of-type(${index + 1}) svg path:first-of-type`,
      { strokeDasharray: LEGEND_DASH_PATTERN },
    ])
  )
);

// Round to a 1, 2 or 5 step so ticks land on clean values.
const niceDomain = (min: number, max: number): { min: number; max: number } => {
  const span = max - min || Math.abs(max) || 1;
  const roughStep = span / TARGET_TICK_COUNT;
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const step = ([1, 2, 5, 10].find((factor) => factor * magnitude >= roughStep) ?? 10) * magnitude;
  const clean = (value: number) => Number(value.toPrecision(12));

  return { min: clean(Math.floor(min / step + 1e-9) * step), max: clean(Math.ceil(max / step - 1e-9) * step) };
};

const TrendLineChart: React.FC<TrendLineChartProps> = ({
  points,
  series,
  yAxisLabel,
  valueFormatter,
  height = 320,
  yAxisMin = 'auto',
  area = 'none',
  showMarks = 'auto',
  hiddenIds: controlledHiddenIds,
  onToggleSeries,
  legend = true,
  lineChartProps,
  children,
}) => {
  const intl = useIntl();
  const palette = useChartPalette();
  const chartSkipAnimation = useChartAnimation();
  const { highlightedItem, onHighlightChange, onLegendItemHover, onLegendItemLeave } = useLegendHighlight();

  const [internalHiddenIds, setInternalHiddenIds] = useState<string[]>([]);
  const hiddenIds = controlledHiddenIds ?? internalHiddenIds;
  const toggle = (id: string) => {
    if (controlledHiddenIds === undefined) {
      setInternalHiddenIds((current) => (current.includes(id) ? current.filter((hiddenId) => hiddenId !== id) : [...current, id]));
    }
    onToggleSeries?.(id);
  };

  const visibleSeries = useMemo(() => series.filter((item) => !hiddenIds.includes(item.id)), [series, hiddenIds]);
  const allSeriesHidden = series.length > 0 && visibleSeries.length === 0;
  const soloVisibleSeries = visibleSeries.length === 1 ? visibleSeries[0] : undefined;
  const marksOn = showMarks === 'auto' ? points.length <= AUTO_MARKS_MAX_POINTS : showMarks;

  const formatValue = (value: number | null): string | null => {
    if (value === null) return null;
    return valueFormatter ? valueFormatter(value) : intl.formatNumber(value);
  };

  // Derive the y domain from every series so scale and ticks stay put when lines are hidden.
  const yDomain = useMemo(() => {
    const values = series.flatMap((item) => item.data).filter((value): value is number => value !== null);
    if (values.length === 0) return undefined;

    const floor = yAxisMin === 'zero' ? [0] : [];
    return niceDomain(Math.min(...values, ...floor), Math.max(...values, ...floor));
  }, [series, yAxisMin]);

  const gradient = useChartGradient(soloVisibleSeries?.color ?? palette.series.categorical[0], [
    { offset: '0%', opacity: 0.32 },
    { offset: '100%', opacity: 0 },
  ]);

  const ridgeGradientBaseId = useId().replace(/:/g, '');
  const ridgeGradientDefs = area === 'ridge' && (
    <defs>
      {series.map((item, index) => (
        <linearGradient key={item.id} id={`${ridgeGradientBaseId}-${index}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={item.color} stopOpacity={1} />
          <stop offset="100%" stopColor={darken(item.color, 0.5)} stopOpacity={1} />
        </linearGradient>
      ))}
    </defs>
  );

  // State-dependent SVG rules cannot live in the theme, so they stay here (S11).
  const stateSx: Record<string, Record<string, string | number | undefined>> = {};
  visibleSeries.forEach((item) => {
    if (item.dashed) {
      stateSx[`& .${lineClasses.line}[data-series="${item.id}"]`] = { strokeDasharray: DASH_PATTERN };
    }
  });
  if (area === 'gradient' && soloVisibleSeries) {
    stateSx[`& .${lineClasses.area}[data-series="${soloVisibleSeries.id}"]`] = { fill: gradient.fill };
  }
  if (area === 'ridge') {
    series.forEach((item, index) => {
      stateSx[`& .${lineClasses.area}[data-series="${item.id}"]`] = { fill: `url(#${ridgeGradientBaseId}-${index})` };
    });
  }
  if (allSeriesHidden) {
    // Keep the hidden-series message at full contrast while the axis labels dim.
    stateSx['& text:not(.chart-hidden-series-overlay-text)'] = { opacity: 0.4 };
  }

  const chartSeries = visibleSeries.map((item) => ({
    id: item.id,
    label: item.label,
    color: item.color,
    shape: item.shape,
    data: item.data,
    area: area !== 'none',
    // Lines bridge missing buckets. The values stay null, so the tooltip still omits them.
    connectNulls: true,
    showMark: marksOn,
    valueFormatter: formatValue,
    curve: 'natural' as const,
    ...SERIES_HIGHLIGHT,
  }));

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <LineChart
        height={height}
        skipAnimation={chartSkipAnimation}
        hideLegend
        xAxis={[{ data: points, scaleType: 'point' }]}
        yAxis={[
          {
            scaleType: 'linear',
            label: yAxisLabel,
            ...(yDomain && { domainLimit: () => yDomain }),
          },
        ]}
        series={chartSeries}
        highlightedItem={highlightedItem}
        onHighlightChange={onHighlightChange}
        slots={{ noDataOverlay: BenchmarkChartHiddenSeriesOverlay, tooltip: BenchmarkChartTooltip }}
        sx={stateSx}
        {...lineChartProps}>
        {children}
        {gradient.defs}
        {ridgeGradientDefs}
      </LineChart>
      {legend && (
        <LegendFrame dashedIndexes={series.flatMap((item, index) => (item.dashed ? [index] : []))}>
          <ChartLegend
            items={series.map((item) => ({ id: item.id, label: item.label, color: item.color, shape: item.shape }))}
            hiddenIds={hiddenIds}
            onToggle={toggle}
            onItemHover={onLegendItemHover}
            onItemLeave={onLegendItemLeave}
          />
        </LegendFrame>
      )}
    </Box>
  );
};

export default TrendLineChart;
